 import { useEffect, useMemo, useRef } from "react";
 import ReCAPTCHA from "react-google-recaptcha";
import type { ChangeHandler } from "react-hook-form";
import { useForm } from "react-hook-form";

import clsx from "clsx";

import type { ICommonServiceOutput } from "@/Components/Api/CommonServiceOutput";
import Typography from "@/design-system/Typography";
import Button from "@/design-system/button";
import Checkbox from "@/design-system/checkbox";
import ErrorMessage from "@/design-system/error-message";
import ReadMoreText from "@/design-system/read-more-text/read-more-text";
import TextInput from "@/design-system/text-input";
import { sendTelemetryEvent, type ITelemetryCustom } from "@/lib/telemetry";
import { useShowRegistrationOnboardingContext, useUserAuthenticationContext } from "@/src/contexts";
import type { ILoginResult } from "@/src/modals/LoginResult";
import type { LoginAndRegistrationFormResources } from "@/src/modals/login-registration/LoginAndRegistrationModal";
import type { ConsentScreenType, IConsentItem } from "@/src/modals/useConsents";
import * as portalCss from "@/src/shared/Portal.module.css";
import useAjax, { AjaxMethod } from "@Components/Hooks/useAjax";
import { Loading } from "@Components/Loading";
import {
    deleteCookie,
    getCookie,
    sendToGTMEventClick,
} from "@Components/Modules/Helpers";
import { isNullOrEmpty } from "@Components/Modules/Validations";
import { LoginOrRegistration } from "@Routes/AccountLinks";
import * as stylex from "@stylexjs/stylex";

import { colors } from "../../../styles/tokens.stylex";

import * as css from "./LoginAndRegistrationForm.module.css";

export type ILoginAndRegistrationForm = {
    consents: IConsentItem[];
    reCaptchaPublicToken: string;
    resources: LoginAndRegistrationFormResources;
    isRegistration: boolean;
    formTitle: string;
    consentScreenType: ConsentScreenType;
    noSocialLoginError?: string;
    onConsentStatusChange: ChangeHandler;
    forgetPasswordOpen: () => void;
    setEmailToMigrate?: (email: string) => void;
    onSuccess?: () => void;
    bypassOnboardingScreen?: boolean;
    channel?: "Plugin";
    fingerprint: string;
    customRegisterButtonTelemetry?:ITelemetryCustom;
};

type FormValues = {
    email?: string;
    password?: string;
    captchaToken?: string;
    consents: { [key: string]: boolean };
};

export default function LoginAndRegistrationForm({
    consents,
    reCaptchaPublicToken,
    resources,
    isRegistration,
    formTitle,
    consentScreenType,
    forgetPasswordOpen,
    onConsentStatusChange,
    setEmailToMigrate,
    onSuccess,
    bypassOnboardingScreen,
    channel,
    fingerprint,
    customRegisterButtonTelemetry,
}: ILoginAndRegistrationForm) {
    const { refreshUserContext } = useUserAuthenticationContext();
    const { setShowRegistrationOnboarding } = useShowRegistrationOnboardingContext();
    const recaptchaRef = useRef<ReCAPTCHA>();
    const formRef = useRef<HTMLFormElement>();

    const loginError = useMemo(() => {
        const value = getCookie("externalAuthenticationError");
        if (value) {
            deleteCookie("externalAuthenticationError");
            return resources.socialLoginError;
        }

        return undefined;
    }, [consentScreenType]);

    const { register, handleSubmit, formState, setValue, setError, clearErrors, reset, getValues } =
        useForm<FormValues>({
            defaultValues: {
                captchaToken: "",
                email: "",
                password: "",
                consents: consents?.reduce(
                    (prev, curr) => ({ ...prev, [`${curr.id}`]: curr.isSelectedByDefault }),
                    {},
                ),
            },
        });

    const resetCaptcha = () => {
        recaptchaRef?.current?.reset();
        setValue("captchaToken", null);
    };

    const { request, data, isLoading } = useAjax<ICommonServiceOutput<ILoginResult>>({
        ajaxAction: LoginOrRegistration,
        ajaxMethod: AjaxMethod.Post,
        success: (response) => {
            if (response.resultStatus?.success === true) {
                if (onSuccess) {
                    onSuccess();
                }

                if (isRegistration && !bypassOnboardingScreen ) {
                        setShowRegistrationOnboarding(true);
                } else {
                    if (response.data?.shouldRefresh) {
                        window.location.reload();
                        return;
                    }
                }

                sendTelemetryEvent({
                    eventName: `${isRegistration ? "rgstr" : "login"}-Email`,
                    actionType: "Click",
                });

                refreshUserContext();
            }

            resetCaptcha();

            if (response.data?.userToMigrate || response.data?.userHasSocialAccount) {
                setEmailToMigrate(getValues("email"));
            }

            response.resultStatus.validationErrors?.forEach((element) => {
                if (element.name === "CaptchaToken") {
                    setError("captchaToken", { type: "custom", message: element.message });
                } else if (element.name) {
                    setError(element.name as keyof FormValues, { message: element.message });
                } else {
                    setError("root", { message: element.message });
                }
            });
        },
    });

    const onClick = useMemo(
        () =>
            isRegistration
                ? () => {
                      sendToGTMEventClick("common_registerIn");
                      sendTelemetryEvent({
                          eventName: "common-registerEmail",
                          actionType: "Click",
                          channel,
                      });
                      sendTelemetryEvent(customRegisterButtonTelemetry)

                  }
                : () => sendToGTMEventClick("common_login"),
        [channel, isRegistration,customRegisterButtonTelemetry],
    );

    useEffect(() => {
        reset();
        resetCaptcha();

        if (data?.resultStatus?.errorMessage) {
            data.resultStatus.errorMessage = "";
        }
    }, [isRegistration]);

    const onSubmit = (values: FormValues) => {
        if (reCaptchaPublicToken && isNullOrEmpty(values.captchaToken)) {
            setError("captchaToken", { type: "required" });
            return;
        }

        request(
            null,
            {
                ...values,
                isRegistration,
                password: btoa(encodeURIComponent(values.password)),
                consentScreenType,
                fgp: fingerprint,
                origin: channel,
            },
            { platform: channel },
        );
    };

    useEffect(() => {
        consents?.forEach((value) => {
            const fieldName = `consents.${value.id}` as `consents.${string}`;
            const inputsList = formRef.current.getElementsByTagName("input");
            const checkbox = inputsList.namedItem(fieldName);

            if (checkbox?.checked === !value.isSelectedByDefault) {
                setValue(fieldName, value.isSelectedByDefault);
            }
        });
    }, [consents]);

    return (
        <form className={css.loginAndRegistrationForm} onSubmit={handleSubmit(onSubmit)} ref={formRef}>
            <h5 dangerouslySetInnerHTML={{ __html: formTitle }} />

            {formState.errors.root && <p className={portalCss.hasError}>{formState.errors.root.message}</p>}
            {data?.resultStatus?.errorMessage && (
                <p className={portalCss.hasError}>{data.resultStatus.errorMessage}</p>
            )}
            {loginError && <p className={portalCss.hasError}>{loginError}</p>}

            <TextInput
                placeholder={resources.email}
                variant="sm"
                type="text"
                data-testid="login-form-email"
                {...register("email", {
                    required: resources.emailRequired,
                    pattern: {
                        value: /^[a-zA-Z0-9.!#$%&`*+/=?^_{|}~-]+@[a-zA-Z0-9-]+(?:.[a-zA-Z0-9-]+)*$/,
                        message: resources.emailInvalid,
                    },
                    maxLength: {
                        value: 200,
                        message: resources.emailTooLong.replace("{maxSize}", "200"),
                    },
                })}
                errorMessage={formState.errors.email?.message}
                aria-label={resources.email}
            />
            <div>
                <TextInput
                    variant="sm"
                    placeholder={resources.password}
                    type="password"
                    {...register("password", {
                        required: resources.passwordRequired,
                        minLength: { value: 6, message: resources.passwordTooShort },
                    })}
                    errorMessage={formState.errors.password?.message}
                    aria-label={resources.password}
                />
                {!isRegistration && (
                    <Button
                        variant="none"
                        as="button"
                        size="None"
                        type="button"
                        onSubmit={() => false} //to be ignored when pressing "Enter"
                        onClick={forgetPasswordOpen}
                    >
                        <Typography fontStyle="BodyS" styleX={styles.resetPassword}>
                            {resources.forgetPassword}
                        </Typography>
                    </Button>
                )}
            </div>

            {reCaptchaPublicToken && (
                <div
                    id="recaptcha_token"
                    className={clsx(css.captcha, formState.errors.captchaToken?.type && css.hasError)}
                >
                    <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={reCaptchaPublicToken}
                        hl={resources.captchaLanguage}
                        size="normal"
                        onChange={(token) => {
                            clearErrors("captchaToken");
                            setValue("captchaToken", token);
                        }}
                        onExpired={() => setError("captchaToken", { type: "validate" })}
                    />
                    <ErrorMessage errorMessage={formState.errors.captchaToken?.message} />
                </div>
            )}

            {consents?.map((value) =>
                value.isSelectable ? (
                    <Checkbox
                        key={value.id}
                        id={value.id}
                        checked={value.isSelectedByDefault}
                        errorMessage={formState.errors.consents?.[value.id]?.message}
                        {...register(`consents.${value.id}`, {
                            required: value.isRequired && resources.userConsentError,
                            onChange: onConsentStatusChange,
                        })}
                    >
                        <ReadMoreText
                            styleX={styles.readMore}
                            longDescription={value.longDescription}
                            shortDescription={value.shortDescription}
                            longButtonText={resources.showLess}
                            shortButtonText={resources.showMore}
                            startShort
                        />
                    </Checkbox>
                ) : (
                    <ReadMoreText
                        styleX={styles.readMore}
                        key={value.id}
                        longDescription={value.longDescription}
                        shortDescription={value.shortDescription}
                        longButtonText={resources.showLess}
                        shortButtonText={resources.showMore}
                        startShort
                    />
                ),
            )}
            <div className={css.actionContainer}>
                {isLoading ? <Loading  /> : <Button
                    as="button"
                    type="submit"
                    variant="magentaRound"
                    size="M"
                    onClick={onClick}
                    styleX={styles.submitButton}
                    disabled={isLoading}
                    data-testid="acc-submit-button"
                    >
                        {resources.formButton.replace("{ProviderName}", "e-mail")}
                    </Button>}
                </div>
        </form>
    );
}

const styles = stylex.create({
    submitButton: {
        width: "100%",
    },
    resetPassword: {
        margin: "0",
        padding: "0.5rem",
        color: colors.b_BrandMagenta,
        textAlign: "left",
    },
    readMore: {
        fontSize: "0.75rem",
        lineHeight: 1.5,
    },
});
